"use client";

import React, { useState, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import { Star, Telescope, ChevronRight, Heart, Sparkles, Menu, X, Calendar, Zap } from 'lucide-react';
import { AboutSidebar } from '@/components/about/sidebar';

interface AboutLayoutProps {
  children: React.ReactNode;
  activeSection?: 'overview' | 'astrology' | 'tarot' | 'usage-framework';
}

export function AboutLayout({ children, activeSection = 'overview' }: AboutLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [quickAccessMenuOpen, setQuickAccessMenuOpen] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  // Auto-open sidebar on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) { // lg breakpoint
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    // Set initial state
    handleResize();

    // Listen for window resize
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mobile debug console - removed for server safety

  // Determine active section based on pathname
  const getActiveSection = () => {
    if (pathname.includes('/astrology')) return 'astrology';
    if (pathname.includes('/tarot')) return 'tarot';
    if (pathname.includes('/usage-framework')) return 'usage-framework';
    return 'overview';
  };

  const currentSection = activeSection || getActiveSection();

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Mobile Header */}
      <div className="lg:hidden bg-gradient-to-r from-slate-900 via-slate-900 to-slate-800 border-b border-slate-700/50 shadow-xl">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                <Star className="h-4 w-4 text-white" />
              </div>
              <div>
                <Link href="/" className="block">
                  <h1 className="text-lg font-bold text-slate-100 hover:text-orange-300 transition-colors">AXTROLOG</h1>
                </Link>
                <p className="text-xs text-slate-400 -mt-0.5">Bilgi Merkezi</p>
              </div>
            </div>

            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`relative p-3 rounded-xl transition-all duration-300 ${
                sidebarOpen
                  ? 'bg-red-500/20 border border-red-500/30 text-red-400'
                  : 'bg-slate-800/80 border border-slate-600/50 text-slate-300 hover:bg-slate-700/80 hover:border-slate-500/50'
              }`}
            >
              <div className="relative">
                {sidebarOpen ? (
                  <X className="h-5 w-5 transition-transform duration-200" />
                ) : (
                  <Menu className="h-5 w-5 transition-transform duration-200" />
                )}
              </div>
              {!sidebarOpen && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="flex h-screen lg:h-auto">
        {/* Mobile Sidebar Overlay with Blur Effect */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden transition-all duration-300"
            onClick={() => setSidebarOpen(false)}
            style={{
              animation: sidebarOpen ? 'fadeIn 0.3s ease-out' : 'fadeOut 0.3s ease-out'
            }}
          />
        )}

        {/* Modern Mobile Sidebar */}
        <div className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 fixed lg:relative z-50 lg:z-auto transition-all duration-500 ease-out lg:transition-none w-full lg:w-80`}>

          {/* Mobile Sidebar Content */}
          <div className="lg:hidden h-full w-full">
            <div className="h-full w-full bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 shadow-2xl">
              {/* Mobile Sidebar Header */}
              <div className="p-6 border-b border-slate-700/50 bg-gradient-to-r from-slate-800/50 to-slate-900/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                      <Star className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-slate-100">Menü</h2>
                      <p className="text-xs text-slate-400">İçerik kategorileri</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 transition-colors"
                  >
                    <X className="h-4 w-4 text-slate-300" />
                  </button>
                </div>
              </div>

              {/* Modern Mobile Navigation */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {/* Main Navigation Cards */}
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-4">Ana Bölümler</h3>

                  {/* Overview */}
                  <button
                    onClick={() => {
                      console.log('Navigation: Overview');
                      setSidebarOpen(false);
                      setTimeout(() => router.push('/about'), 100);
                    }}
                    className="w-full group"
                  >
                    <div className={`bg-gradient-to-r from-slate-800/80 to-slate-700/40 rounded-xl p-4 border transition-all duration-300 hover:shadow-lg hover:shadow-yellow-500/10 ${
                      currentSection === 'overview'
                        ? 'border-yellow-500/50 bg-yellow-500/10'
                        : 'border-slate-600/30 hover:border-yellow-500/50'
                    }`}>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                          <Star className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <h4 className={`font-semibold transition-colors ${
                            currentSection === 'overview' ? 'text-yellow-300' : 'text-slate-100 group-hover:text-yellow-300'
                          }`}>Genel Bakış</h4>
                          <p className="text-xs text-slate-400 mt-1">AXTROLOG hakkında temel bilgiler</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-yellow-400 group-hover:translate-x-1 transition-all duration-300" />
                      </div>
                    </div>
                  </button>

                  {/* Astrology */}
                  <button
                    onClick={() => {
                      console.log('Navigation: Astrology');
                      setSidebarOpen(false);
                      setTimeout(() => router.push('/about/astrology'), 100);
                    }}
                    className="w-full group"
                  >
                    <div className={`bg-gradient-to-r from-slate-800/80 to-slate-700/40 rounded-xl p-4 border transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10 ${
                      currentSection === 'astrology'
                        ? 'border-blue-500/50 bg-blue-500/10'
                        : 'border-slate-600/30 hover:border-blue-500/50'
                    }`}>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                          <Telescope className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <h4 className={`font-semibold transition-colors ${
                            currentSection === 'astrology' ? 'text-blue-300' : 'text-slate-100 group-hover:text-blue-300'
                          }`}>Astroloji</h4>
                          <p className="text-xs text-slate-400 mt-1">Ev sistemleri ve disiplinler</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-blue-400 group-hover:translate-x-1 transition-all duration-300" />
                      </div>
                    </div>
                  </button>

                  {/* Tarot */}
                  <button
                    onClick={() => {
                      console.log('Navigation: Tarot');
                      setSidebarOpen(false);
                      setTimeout(() => router.push('/about/tarot'), 100);
                    }}
                    className="w-full group"
                  >
                    <div className={`bg-gradient-to-r from-slate-800/80 to-slate-700/40 rounded-xl p-4 border transition-all duration-300 hover:shadow-lg hover:shadow-pink-500/10 ${
                      currentSection === 'tarot'
                        ? 'border-pink-500/50 bg-pink-500/10'
                        : 'border-slate-600/30 hover:border-pink-500/50'
                    }`}>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                          <Heart className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <h4 className={`font-semibold transition-colors ${
                            currentSection === 'tarot' ? 'text-pink-300' : 'text-slate-100 group-hover:text-pink-300'
                          }`}>Tarot</h4>
                          <p className="text-xs text-slate-400 mt-1">Sistem açıklamaları</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-pink-400 group-hover:translate-x-1 transition-all duration-300" />
                      </div>
                    </div>
                  </button>

                  {/* Usage Framework */}
                  <button
                    onClick={() => {
                      console.log('Navigation: Usage Framework');
                      setSidebarOpen(false);
                      setTimeout(() => router.push('/about/usage-framework'), 100);
                    }}
                    className="w-full group"
                  >
                    <div className={`bg-gradient-to-r from-slate-800/80 to-slate-700/40 rounded-xl p-4 border transition-all duration-300 hover:shadow-lg hover:shadow-slate-400/10 ${
                      currentSection === 'usage-framework'
                        ? 'border-slate-400/50 bg-slate-400/10'
                        : 'border-slate-600/30 hover:border-slate-400/50'
                    }`}>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-slate-500 to-slate-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                          <Sparkles className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <h4 className={`font-semibold transition-colors ${
                            currentSection === 'usage-framework' ? 'text-slate-300' : 'text-slate-100 group-hover:text-slate-300'
                          }`}>Kullanım Çerçevesi</h4>
                          <p className="text-xs text-slate-400 mt-1">Koşullar ve sınırlamalar</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-slate-300 group-hover:translate-x-1 transition-all duration-300" />
                      </div>
                    </div>
                  </button>
                </div>

                {/* Quick Access Button */}
                <div className="pt-4 mt-4 border-t border-slate-700/30">
                  <button
                    onClick={() => {
                      console.log('Opening Quick Access Menu');
                      setQuickAccessMenuOpen(true);
                    }}
                    className="w-full group"
                  >
                    <div className="bg-gradient-to-r from-slate-800/80 to-slate-700/40 rounded-xl p-4 border border-slate-600/30 hover:border-emerald-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/10">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                          <Zap className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <h4 className="font-semibold transition-colors text-slate-100 group-hover:text-emerald-300">⚡ Hızlı Erişim</h4>
                          <p className="text-xs text-slate-400 mt-1">Alt sayfa linkleri</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all duration-300" />
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Mobile Footer */}
              <div className="p-4 border-t border-slate-700/50 bg-gradient-to-r from-slate-800/30 to-slate-900/30">
                <div className="text-center">
                  <p className="text-xs text-slate-500">AXTROLOG</p>
                  <p className="text-xs text-slate-600">Bilgilendirme Amaçlı</p>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Sidebar (unchanged) */}
          <div className="hidden lg:block">
            <AboutSidebar
              activeSection={currentSection}
              onLinkClick={() => {
                console.log('onLinkClick called - closing sidebar');
                setSidebarOpen(false);
              }}
              onSectionChange={(section) => {
                console.log('onSectionChange called:', section);
              }}
            />
          </div>
        </div>

        {/* Quick Access Menu - Separate Full Screen */}
        {quickAccessMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-60 lg:hidden transition-all duration-300"
              onClick={() => setQuickAccessMenuOpen(false)}
            />

            {/* Quick Access Menu */}
            <div className="fixed inset-0 z-70 lg:hidden">
              <div className="h-full w-full bg-gradient-to-b from-emerald-900/20 via-slate-900 to-slate-900 animate-in slide-in-from-right-full duration-300">
                {/* Header */}
                <div className="p-6 border-b border-emerald-700/50 bg-gradient-to-r from-emerald-800/30 to-slate-900/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                        <Zap className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-emerald-200">⚡ Hızlı Erişim</h2>
                        <p className="text-xs text-slate-400">Alt sayfa linkleri</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setQuickAccessMenuOpen(false)}
                      className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 transition-colors"
                    >
                      <X className="h-4 w-4 text-slate-300" />
                    </button>
                  </div>
                </div>

                {/* Quick Access Links */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {/* Astrology Section */}
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 bg-blue-500/80 rounded-lg flex items-center justify-center">
                        <Telescope className="h-4 w-4 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-blue-300">🔭 Astroloji</h3>
                    </div>
                    <div className="space-y-3">
                      <button
                        onClick={() => {
                          console.log('Quick link clicked: Ev Sistemleri');
                          setQuickAccessMenuOpen(false);
                          setSidebarOpen(false);
                          setTimeout(() => router.push('/about/astrology/house-systems'), 100);
                        }}
                        className="w-full flex items-center gap-4 p-4 rounded-xl bg-slate-800/40 hover:bg-slate-700/50 border border-slate-700/30 hover:border-blue-500/50 transition-all duration-200"
                      >
                        <div className="w-10 h-10 bg-blue-500/70 rounded-lg flex items-center justify-center">
                          <Calendar className="h-5 w-5 text-white" />
                        </div>
                        <div className="text-left">
                          <h4 className="text-slate-200 font-medium">Ev Sistemleri</h4>
                          <p className="text-xs text-slate-400">Placidus, Koch, Equal House</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-400 ml-auto" />
                      </button>
                      <button
                        onClick={() => {
                          console.log('Quick link clicked: Astroloji Disiplinleri');
                          setQuickAccessMenuOpen(false);
                          setSidebarOpen(false);
                          setTimeout(() => router.push('/about/astrology/disciplines'), 100);
                        }}
                        className="w-full flex items-center gap-4 p-4 rounded-xl bg-slate-800/40 hover:bg-slate-700/50 border border-slate-700/30 hover:border-purple-500/50 transition-all duration-200"
                      >
                        <div className="w-10 h-10 bg-purple-500/70 rounded-lg flex items-center justify-center">
                          <Telescope className="h-5 w-5 text-white" />
                        </div>
                        <div className="text-left">
                          <h4 className="text-slate-200 font-medium">Astroloji Disiplinleri</h4>
                          <p className="text-xs text-slate-400">Doğum, Transit, Progresyon</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-400 ml-auto" />
                      </button>
                      <button
                        onClick={() => {
                          console.log('Quick link clicked: Gelişmiş Sistemler');
                          setQuickAccessMenuOpen(false);
                          setSidebarOpen(false);
                          setTimeout(() => router.push('/about/astrology/advanced-systems'), 100);
                        }}
                        className="w-full flex items-center gap-4 p-4 rounded-xl bg-slate-800/40 hover:bg-slate-700/50 border border-slate-700/30 hover:border-indigo-500/50 transition-all duration-200"
                      >
                        <div className="w-10 h-10 bg-indigo-500/70 rounded-lg flex items-center justify-center">
                          <Zap className="h-5 w-5 text-white" />
                        </div>
                        <div className="text-left">
                          <h4 className="text-slate-200 font-medium">Gelişmiş Sistemler</h4>
                          <p className="text-xs text-slate-400">Harmonikler, Midpoint</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-400 ml-auto" />
                      </button>
                    </div>
                  </div>

                  {/* Tarot Section */}
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 bg-pink-500/80 rounded-lg flex items-center justify-center">
                        <Heart className="h-4 w-4 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-pink-300">🃏 Tarot</h3>
                    </div>
                    <div className="space-y-3">
                      <button
                        onClick={() => {
                          console.log('Quick link clicked: Tarot Sistemleri');
                          setQuickAccessMenuOpen(false);
                          setSidebarOpen(false);
                          setTimeout(() => router.push('/about/tarot/systems'), 100);
                        }}
                        className="w-full flex items-center gap-4 p-4 rounded-xl bg-slate-800/40 hover:bg-slate-700/50 border border-slate-700/30 hover:border-pink-500/50 transition-all duration-200"
                      >
                        <div className="w-10 h-10 bg-pink-500/70 rounded-lg flex items-center justify-center">
                          <Star className="h-5 w-5 text-white" />
                        </div>
                        <div className="text-left">
                          <h4 className="text-slate-200 font-medium">Tarot Sistemleri</h4>
                          <p className="text-xs text-slate-400">Rider-Waite, Marseille, Thoth</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-400 ml-auto" />
                      </button>
                      <button
                        onClick={() => {
                          console.log('Quick link clicked: Yorumlama Teknikleri');
                          setQuickAccessMenuOpen(false);
                          setSidebarOpen(false);
                          setTimeout(() => router.push('/about/tarot/interpretation-techniques'), 100);
                        }}
                        className="w-full flex items-center gap-4 p-4 rounded-xl bg-slate-800/40 hover:bg-slate-700/50 border border-slate-700/30 hover:border-teal-500/50 transition-all duration-200"
                      >
                        <div className="w-10 h-10 bg-teal-500/70 rounded-lg flex items-center justify-center">
                          <Star className="h-5 w-5 text-white" />
                        </div>
                        <div className="text-left">
                          <h4 className="text-slate-200 font-medium">Yorumlama Teknikleri</h4>
                          <p className="text-xs text-slate-400">Yayılımlar, Kart Anlamları</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-400 ml-auto" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-emerald-700/30 bg-gradient-to-r from-emerald-800/20 to-slate-900/30">
                  <button
                    onClick={() => setQuickAccessMenuOpen(false)}
                    className="w-full flex items-center justify-center gap-2 p-3 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 border border-slate-600/50 transition-colors"
                  >
                    <X className="h-4 w-4 text-slate-300" />
                    <span className="text-slate-300">Menüyü Kapat</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto lg:pt-0 pt-0">
          {children}
        </div>
      </div>
    </div>
  );
}